/**
 * Eine einzelne Wohneinheit (HM-079, HM-080) — `/anlagen/{id}/wohneinheiten/{unitId}`.
 *
 * **Drei Reiter:** „Strom" zeigt, was die Wohnung im gewählten Zeitraum
 * verbraucht hat und woher es kam — Verbrauch, PV-Anteil, Netzanteil aus dem
 * Messkonzept der Anlage (`UnitElectricityView`, dieselbe Ansicht wie beim
 * Bewohner); „Bewohner" die Bewohnungen und Einladungen (HM-081);
 * „Einstellungen" die Stammdaten, die Messstellen der Einheit und den roten
 * Kasten. **Die Übersicht davor ist entfallen** (HM-156): Sie zeigte dieselben
 * Größen wie „Strom", nur knapper.
 *
 * **Ohne ein Messkonzept, das diese Einheit als Steckplatz führt, ist „Strom"
 * leer und sagt, wo die Werte entstehen.** Die Größen einer
 * Wohnung gibt es nicht aus ihrem Zähler allein: Der PV-Anteil ist eine
 * Rechnung über das ganze Haus (OQ-042).
 *
 * **Name und Fakten stehen in der Kopfleiste des Rahmens** (`unitHeadline`),
 * wie beim Zähler (HM-072) und beim Gerät (HM-073).
 *
 * **Die Messstellen werden hier gezeigt, nicht zugeordnet.** Zugeordnet wird
 * am Zähler, in dessen Stammdaten: Wer vor dem Zählerschrank steht, liest die
 * Wohnung vom Zähler ab. Die Liste hier sagt, was schon dort steht, und führt
 * zum Zähler, der an der Messstelle hängt.
 */
import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Badge,
  Button,
  Card,
  Checkbox,
  Dialog,
  EmptyState,
  Input,
  SegmentedControl,
  icons,
} from "@hausmeter/ui";
import { formatInstant } from "@hausmeter/i18n";
import type { ApiComponents } from "@hausmeter/sdk";
import { api } from "../lib/client.js";
import { apiErrorMessage } from "../lib/api-error.js";
import { DefinitionList } from "../components/definition-list.js";
import { Section } from "../components/section.js";
import { listRow, mutedText, plainList, rowLabel } from "../lib/list-styles.js";
import { navigate, routePath, type EnergyPeriod, type Route, type UnitTab } from "../lib/router.js";
import { useMeteringPoints, useMeters, useSiteArea, useSiteAreas, useUnitResidents } from "../lib/queries.js";
import { formatDate } from "../lib/format.js";
import { UnitElectricityView } from "./unit-electricity.js";
import { parseArea } from "./units.js";
import { endedInvitation, useSiteAccess } from "../lib/site-access.js";

type SiteArea = ApiComponents["schemas"]["SiteArea"];
type Row = readonly [string, string];


export function UnitDetailScreen({
  siteId,
  unitId,
  tab,
  period,
  date,
  siteTimeZone,
}: {
  siteId: string;
  unitId: string;
  tab: UnitTab;
  period: EnergyPeriod;
  date: string | null;
  siteTimeZone: string;
}) {
  const area = useSiteArea(unitId);
  const listRoute: Route = { kind: "site", siteId, area: "units" };
  const [editing, setEditing] = useState(false);
  const tabRoute = (next: UnitTab): Route => ({ kind: "unit", siteId, unitId, tab: next, period, date });
  /* Was die Teilung nicht erlaubt, fehlt (HM-160, Regel 10). Bewohner verwaltet
     nur der Besitzer — eine Adresse auf den Reiter zeigt dann „Strom". */
  const rights = useSiteAccess();
  const shownTab: UnitTab = tab === "residents" && !rights.canManage ? "electricity" : tab;

  if (area.isPending) return <Card padding="sm">Wird geladen …</Card>;

  if (area.isError || area.data === undefined || area.data.siteId !== siteId) {
    return (
      <Card>
        <EmptyState title="Wohneinheit nicht gefunden" icon={<icons.IconDoor size={30} />}>
          Diese Wohneinheit gibt es an dieser Anlage nicht — vielleicht wurde sie gelöscht oder
          gehört zu einer anderen.
          <div style={{ marginTop: "var(--sp-3)" }}>
            <Button variant="secondary" size="sm" onClick={() => navigate(listRoute)}>
              Zurück zu den Wohneinheiten
            </Button>
          </div>
        </EmptyState>
      </Card>
    );
  }

  const data = area.data;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20, maxWidth: 1100 }}>
      {rights.canWrite && <UnitEditDialog area={data} open={editing} onClose={() => setEditing(false)} />}

      <SegmentedControl
        label="Ansicht"
        size="sm"
        value={shownTab}
        onChange={(next) => navigate(tabRoute(next as UnitTab))}
        options={[
          { value: "electricity", label: "Strom" },
          ...(rights.canManage ? [{ value: "residents", label: "Bewohner" }] : []),
          { value: "settings", label: "Einstellungen" },
        ]}
      />

      {/* „Strom" hat den Zeitraum aus der Adresse (HM-087). */}
      {shownTab === "electricity" && (
        <UnitElectricityView
          siteAreaId={data.id}
          unitName={data.name}
          period={period}
          date={date}
          timeZone={siteTimeZone}
          onConcept={() => navigate({ kind: "site", siteId, area: "concept" })}
        />
      )}
      {shownTab === "residents" && <ResidentsSection siteId={siteId} area={data} timeZone={siteTimeZone} />}
      {shownTab === "settings" && (
        <>
          <MeteringPointsSection siteId={siteId} area={data} />
          <FactsSection
            area={data}
            timeZone={siteTimeZone}
            onEdit={rights.canWrite ? () => setEditing(true) : undefined}
          />
          {rights.canDelete && <DangerSection area={data} listRoute={listRoute} />}
        </>
      )}
    </div>
  );
}

/* --------------------------------------------------------------------------
 * Bewohner · Wer wohnt hier, seit wann, und wer ist eingeladen?
 * ----------------------------------------------------------------------- */

/**
 * Die Bewohner einer Wohneinheit (HM-081): die Bewohnungen mit Ein- und
 * Auszug, die offenen Einladungen, und das Formular zum Einladen. **Der
 * Auszug wird gesetzt, nicht die Bewohnung gelöscht** — wer ausgezogen ist,
 * sieht seine Zeit weiter, und die Abrechnung der Wohnung braucht die Grenze.
 */
function ResidentsSection({ siteId, area, timeZone }: { siteId: string; area: SiteArea; timeZone: string }) {
  const client = useQueryClient();
  const residents = useUnitResidents(area.id);
  const [email, setEmail] = useState("");
  const [movedInOn, setMovedInOn] = useState("");
  const [movedOutOn, setMovedOutOn] = useState("");
  const [error, setError] = useState<string | null>(null);
  const refresh = () => client.invalidateQueries({ queryKey: ["unit-residents", area.id] });

  const invite = useMutation({
    mutationFn: async () => {
      const { data, error: failure } = await api.POST("/sites/{siteId}/site-areas/{siteAreaId}/invitations", {
        params: { path: { siteId, siteAreaId: area.id } },
        body: { email: email.trim(), movedInOn, ...(movedOutOn.length > 0 ? { movedOutOn } : {}) },
      });
      if (failure || !data) throw new Error(apiErrorMessage(failure) ?? "Die Einladung ließ sich nicht verschicken.");
      return data;
    },
    onSuccess: async () => {
      setEmail("");
      setMovedInOn("");
      setMovedOutOn("");
      await refresh();
    },
    onError: (failure: Error) => setError(failure.message),
  });
  const revoke = useMutation({
    mutationFn: async (invitationId: string) => {
      const { error: failure } = await api.POST("/invitations/{invitationId}/revoke", { params: { path: { invitationId } } });
      if (failure) throw new Error(apiErrorMessage(failure) ?? "Die Einladung ließ sich nicht zurücknehmen.");
    },
    onSuccess: refresh,
    onError: (failure: Error) => setError(failure.message),
  });
  const moveOut = useMutation({
    mutationFn: async (input: { occupancyId: string; movedOutOn: string | null }) => {
      const { error: failure } = await api.PATCH("/occupancies/{occupancyId}", {
        params: { path: { occupancyId: input.occupancyId } },
        body: { movedOutOn: input.movedOutOn },
      });
      if (failure) throw new Error(apiErrorMessage(failure) ?? "Der Auszug ließ sich nicht setzen.");
    },
    onSuccess: refresh,
    onError: (failure: Error) => setError(failure.message),
  });

  const valid = email.trim().length > 3 && /^\d{4}-\d{2}-\d{2}$/.test(movedInOn) && (movedOutOn.length === 0 || movedOutOn >= movedInOn);

  return (
    <>
      <Section title="Bewohner" subtitle="Wer die Werte dieser Wohnung sehen darf — für die Zeit zwischen Einzug und Auszug.">
        <Card>
          {residents.isPending && <p style={mutedText}>Wird geladen …</p>}
          {residents.data !== undefined && residents.data.occupancies.length === 0 && (
            <p style={mutedText}>Noch niemand wohnt hier. Lade unten eine Person per E-Mail ein.</p>
          )}
          {residents.data !== undefined && residents.data.occupancies.length > 0 && (
            <ul style={plainList}>
              {residents.data.occupancies.map((occupancy) => (
                <OccupancyRow
                  key={occupancy.id}
                  occupancy={occupancy}
                  timeZone={timeZone}
                  busy={moveOut.isPending}
                  onMoveOut={(date) => moveOut.mutate({ occupancyId: occupancy.id, movedOutOn: date })}
                />
              ))}
            </ul>
          )}
        </Card>
      </Section>

      {residents.data !== undefined && residents.data.invitations.length > 0 && (
        <Section title="Einladungen" subtitle="Verschickt und noch offen, abgelehnt oder abgelaufen. Eine Einladung gilt 14 Tage.">
          <Card>
            <ul style={plainList}>
              {residents.data.invitations.map((invitation) => {
                const ended = endedInvitation(invitation.status);
                return (
                  <li key={invitation.id} style={{ ...listRow, borderTop: "none", borderBottom: "1px solid var(--border-subtle)" }}>
                    <span style={{ ...rowLabel, minWidth: "min(220px, 100%)", overflowWrap: "anywhere" }}>{invitation.email}</span>
                    <span style={mutedText}>
                      ab {formatDate(invitation.movedInOn)}
                      {invitation.movedOutOn === null ? "" : ` bis ${formatDate(invitation.movedOutOn)}`} ·{" "}
                      {ended !== null
                        ? ended.hint
                        : `gültig bis ${formatInstant(new Date(invitation.expiresAt), timeZone, "de-DE", "date")}`}
                    </span>
                    <span style={{ marginLeft: "auto" }}>
                      {ended !== null ? (
                        <Badge>{ended.badge}</Badge>
                      ) : (
                        <Button variant="ghost" size="sm" disabled={revoke.isPending} onClick={() => revoke.mutate(invitation.id)}>
                          Zurücknehmen
                        </Button>
                      )}
                    </span>
                  </li>
                );
              })}
            </ul>
          </Card>
        </Section>
      )}

      <Section title="Bewohner einladen" subtitle="Der Eingeladene legt ein Konto an oder meldet sich mit einem bestehenden an; die Adresse muss dieselbe sein.">
        <Card>
          <form
            style={{ display: "flex", flexDirection: "column", gap: "var(--sp-3)" }}
            onSubmit={(event) => {
              event.preventDefault();
              setError(null);
              invite.mutate();
            }}
          >
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 200px), 1fr))", gap: "var(--sp-3)" }}>
              <Input label="E-Mail-Adresse" type="email" value={email} required onChange={(e) => setEmail(e.target.value)} />
              <Input label="Einzug" type="date" value={movedInOn} required onChange={(e) => setMovedInOn(e.target.value)} hint="Ab diesem Tag sieht die Person die Werte." />
              <Input label="Auszug" type="date" value={movedOutOn} onChange={(e) => setMovedOutOn(e.target.value)} hint="Freiwillig; lässt sich später setzen." />
            </div>
            {error !== null && <Alert tone="danger">{error}</Alert>}
            <div>
              <Button type="submit" disabled={!valid || invite.isPending}>
                {invite.isPending ? "Wird verschickt …" : "Einladung verschicken"}
              </Button>
            </div>
          </form>
        </Card>
      </Section>
    </>
  );
}

/** Eine Bewohnung: Name, Zeitraum, und der Auszug als Datum in der Zeile. */
function OccupancyRow({
  occupancy,
  timeZone,
  busy,
  onMoveOut,
}: {
  occupancy: ApiComponents["schemas"]["UnitOccupancy"];
  timeZone: string;
  busy: boolean;
  onMoveOut: (date: string | null) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [date, setDate] = useState(occupancy.movedOutOn ?? "");
  const name = [occupancy.user.firstName, occupancy.user.lastName].filter((part) => part !== null && part.length > 0).join(" ");
  void timeZone;

  return (
    <li style={{ ...listRow, borderTop: "none", borderBottom: "1px solid var(--border-subtle)" }}>
      <span style={{ minWidth: "min(220px, 100%)", display: "flex", flexDirection: "column" }}>
        <span style={rowLabel}>{name.length > 0 ? name : occupancy.user.email}</span>
        {name.length > 0 && <span style={{ ...mutedText, fontSize: 12 }}>{occupancy.user.email}</span>}
      </span>
      <span style={mutedText}>
        seit {formatDate(occupancy.movedInOn)}
        {occupancy.movedOutOn === null ? "" : ` · ausgezogen am ${formatDate(occupancy.movedOutOn)}`}
      </span>
      <span style={{ marginLeft: "auto", display: "flex", gap: "var(--sp-2)", alignItems: "center", flexWrap: "wrap" }}>
        {editing ? (
          <>
            <Input label="Auszug" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
            <Button size="sm" disabled={busy || date.length === 0} onClick={() => { onMoveOut(date); setEditing(false); }}>
              Übernehmen
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>
              Abbrechen
            </Button>
          </>
        ) : occupancy.movedOutOn === null ? (
          <Button size="sm" variant="secondary" disabled={busy} onClick={() => setEditing(true)}>
            Auszug setzen …
          </Button>
        ) : (
          <Button size="sm" variant="ghost" disabled={busy} onClick={() => onMoveOut(null)}>
            Auszug aufheben
          </Button>
        )}
      </span>
    </li>
  );
}

/* --------------------------------------------------------------------------
 * Die Messstellen dieser Einheit
 * ----------------------------------------------------------------------- */

/**
 * **Die Messstelle und der Zähler, der gerade an ihr hängt.** Die Zuordnung
 * gilt der Messstelle; was der Mensch sucht, ist der Zähler. Beide in einer
 * Zeile, mit dem Zähler als Link — ein Nachfolger nach einem Zählerwechsel
 * steht dann von selbst hier, ohne dass jemand die Wohnung neu zuordnet.
 */
function MeteringPointsSection({ siteId, area }: { siteId: string; area: SiteArea }) {
  const points = useMeteringPoints(siteId);
  const meters = useMeters(siteId);
  const mine = (points.data?.items ?? []).filter((point) => point.siteAreaId === area.id);
  const rights = useSiteAccess();

  return (
    <Section
      title="Messstellen"
      subtitle="Was in dieser Wohneinheit gemessen wird. Zugeordnet wird am Zähler, in seinen Stammdaten."
    >
      <Card>
        {points.isPending && <p style={mutedText}>Wird geladen …</p>}
        {!points.isPending && mine.length === 0 && (
          <p style={mutedText}>
            Noch keine Messstelle zugeordnet.
            {rights.canWrite &&
              " Öffne den Zähler der Wohnung und wähle unter „Stammdaten ändern\" diese Wohneinheit."}
          </p>
        )}
        {mine.length > 0 && (
          <ul style={plainList}>
            {mine.map((point) => {
              const meter = (meters.data?.items ?? []).find(
                (entry) => entry.meteringPointId === point.id,
              );
              const route: Route | null =
                meter === undefined
                  ? null
                  : { kind: "meter", siteId, meterId: meter.id, tab: "overview", register: null, range: "24h" };
              return (
                <li key={point.id} style={{ ...listRow, borderTop: "none", borderBottom: "1px solid var(--border-subtle)" }}>
                  <span style={rowLabel}>{point.label}</span>
                  {route !== null && meter !== undefined ? (
                    <a
                      href={routePath(route)}
                      onClick={(event) => {
                        if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) {
                          return;
                        }
                        event.preventDefault();
                        navigate(route);
                      }}
                      style={{ fontSize: 13, color: "var(--text-strong)" }}
                    >
                      {meter.label}
                    </a>
                  ) : (
                    /* Eine Messstelle ohne hängenden Zähler — nach einem Ausbau
                       ohne Nachfolger. Sie bleibt, denn die Historie hängt an ihr. */
                    <span style={mutedText}>zurzeit ohne Zähler</span>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </Card>
    </Section>
  );
}

/* --------------------------------------------------------------------------
 * Stammdaten
 * ----------------------------------------------------------------------- */

function FactsSection({
  area,
  timeZone,
  onEdit,
}: {
  area: SiteArea;
  timeZone: string;
  /** Fehlt, wenn die Teilung kein Ändern erlaubt (HM-160). */
  onEdit?: (() => void) | undefined;
}) {
  const rows: Row[] = [["Bezeichnung", area.name]];
  if (area.location !== null) rows.push(["Lage", area.location]);
  if (area.livingAreaSqm !== null) {
    rows.push([
      "Wohnfläche",
      `${new Intl.NumberFormat("de-DE", { maximumFractionDigits: 2 }).format(area.livingAreaSqm)} m²`,
    ]);
  }
  rows.push(["Selbstgenutzt", area.selfOccupied ? "ja" : "nein"]);
  rows.push(["Angelegt", formatInstant(new Date(area.createdAt), timeZone, "de-DE", "date")]);

  return (
    <Section
      title="Stammdaten"
      subtitle="Was über diese Wohneinheit feststeht. Die Wohnfläche geht in keine Rechnung ein."
      action={
        onEdit !== undefined ? (
          <Button variant="secondary" size="sm" onClick={onEdit}>
            Ändern
          </Button>
        ) : undefined
      }
    >
      <Card>
        <DefinitionList rows={rows} />
      </Card>
    </Section>
  );
}

/**
 * Stammdaten ändern — als Dialog, wie am Zähler. `null` entfernt Lage und
 * Wohnfläche; ein leeres Feld schickt deshalb `null` und nicht `""`.
 */
function UnitEditDialog({
  area,
  open,
  onClose,
}: {
  area: SiteArea;
  open: boolean;
  onClose: () => void;
}) {
  const client = useQueryClient();
  const siblings = useSiteAreas(area.siteId);
  const otherSelfOccupied = (siblings.data?.items ?? []).some(
    (entry) => entry.selfOccupied && entry.id !== area.id,
  );

  const [name, setName] = useState(area.name);
  const [location, setLocation] = useState(area.location ?? "");
  const [livingArea, setLivingArea] = useState(area.livingAreaSqm?.toString() ?? "");
  const [selfOccupied, setSelfOccupied] = useState(area.selfOccupied);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setName(area.name);
    setLocation(area.location ?? "");
    setLivingArea(area.livingAreaSqm?.toString() ?? "");
    setSelfOccupied(area.selfOccupied);
    setError(null);
  }, [open, area.name, area.location, area.livingAreaSqm, area.selfOccupied]);

  const parsed = parseArea(livingArea);

  const save = useMutation({
    mutationFn: async () => {
      const { data, error: failure } = await api.PATCH("/site-areas/{siteAreaId}", {
        params: { path: { siteAreaId: area.id } },
        body: {
          ...(name.trim() !== area.name ? { name: name.trim() } : {}),
          ...((location.trim() || null) !== area.location
            ? { location: location.trim().length > 0 ? location.trim() : null }
            : {}),
          ...(parsed !== undefined && parsed !== area.livingAreaSqm ? { livingAreaSqm: parsed } : {}),
          ...(selfOccupied !== area.selfOccupied ? { selfOccupied } : {}),
        },
      });
      if (failure || !data) {
        throw new Error(apiErrorMessage(failure) ?? "Die Änderung ließ sich nicht speichern.");
      }
      return data;
    },
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["site-areas"] });
      onClose();
    },
    onError: (failure: Error) => setError(failure.message),
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Stammdaten ändern"
      description="Bezeichnung, Lage, Wohnfläche und ob die Einheit selbst bewohnt wird."
      footer={
        <>
          <Button
            type="submit"
            form="unit-stammdaten"
            size="sm"
            disabled={save.isPending || name.trim().length === 0 || parsed === undefined}
          >
            {save.isPending ? "Wird gespeichert …" : "Speichern"}
          </Button>
          <Button type="button" variant="ghost" size="sm" onClick={onClose}>
            Abbrechen
          </Button>
        </>
      }
    >
      <form
        id="unit-stammdaten"
        style={{ display: "flex", flexDirection: "column", gap: "var(--sp-3)" }}
        onSubmit={(event) => {
          event.preventDefault();
          setError(null);
          save.mutate();
        }}
      >
        <Input label="Bezeichnung" value={name} maxLength={120} onChange={(e) => setName(e.target.value)} />
        <Input label="Lage" value={location} maxLength={120} onChange={(e) => setLocation(e.target.value)} />
        <Input
          label="Wohnfläche in m²"
          value={livingArea}
          inputMode="decimal"
          error={parsed === undefined ? "Bitte eine Zahl größer als null." : undefined}
          onChange={(e) => setLivingArea(e.target.value)}
        />
        {/* Ausgeblendet, wenn eine andere Einheit selbstgenutzt ist — der
            Haken antwortete sonst mit `409` (Regel 10). Abwählen geht immer. */}
        {(!otherSelfOccupied || area.selfOccupied) && (
          <Checkbox
            checked={selfOccupied}
            onChange={setSelfOccupied}
            label="Vom Besitzer der Anlage selbst bewohnt"
          />
        )}
        {error !== null && <Alert tone="danger">{error}</Alert>}
      </form>
    </Dialog>
  );
}

/* --------------------------------------------------------------------------
 * Der rote Kasten
 * ----------------------------------------------------------------------- */

/**
 * Löschen — nicht archivieren, anders als Anlage und Zähler: An der Einheit
 * hängen keine Werte. **Solange eine Messstelle auf sie zeigt, gibt es den
 * Kasten nicht** — ein Knopf, der mit `409` antwortet, ist kein Knopf; an
 * seiner Stelle steht, was vorher zu tun ist.
 */
function DangerSection({ area, listRoute }: { area: SiteArea; listRoute: Route }) {
  const client = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const remove = useMutation({
    mutationFn: async () => {
      const { error: failure } = await api.DELETE("/site-areas/{siteAreaId}", {
        params: { path: { siteAreaId: area.id } },
      });
      if (failure) throw new Error(apiErrorMessage(failure) ?? "Löschen fehlgeschlagen.");
    },
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["site-areas"] });
      navigate(listRoute, { replace: true });
    },
    onError: (failure: Error) => setError(failure.message),
  });

  if (area.meteringPointCount > 0) {
    return (
      <Section title="Wohneinheit verwalten">
        <p style={mutedText}>
          Löschen lässt sich diese Wohneinheit erst, wenn ihr keine Messstelle mehr zugeordnet
          ist — die Zuordnung wird am Zähler gelöst.
        </p>
      </Section>
    );
  }

  return (
    <Section title="Wohneinheit verwalten">
      <Card>
        {error !== null && <Alert tone="danger">{error}</Alert>}
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "var(--sp-3)",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "var(--sp-3)",
            border: "1px solid var(--hm-danger)",
            borderRadius: "var(--radius-md)",
          }}
        >
          <span style={{ display: "flex", flexDirection: "column", gap: 2, flex: "1 1 260px" }}>
            <span style={{ fontSize: 13.5, fontWeight: 700, color: "var(--hm-danger)" }}>
              Wohneinheit löschen
            </span>
            <span style={{ fontSize: 12.5, lineHeight: 1.5, color: "var(--text-muted)" }}>
              Löscht diese Wohneinheit. An ihr hängen keine Werte — sie ist eine Zuordnung, und
              die ist gerade leer.
            </span>
          </span>

          <span style={{ display: "flex", gap: "var(--sp-2)", flexWrap: "wrap" }}>
            {confirming ? (
              <>
                <Button
                  variant="danger"
                  size="sm"
                  disabled={remove.isPending}
                  onClick={() => remove.mutate()}
                >
                  {remove.isPending ? "Wird gelöscht …" : "Ja, löschen"}
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setConfirming(false)}>
                  Abbrechen
                </Button>
              </>
            ) : (
              <Button variant="danger" size="sm" onClick={() => setConfirming(true)}>
                Wohneinheit löschen …
              </Button>
            )}
          </span>
        </div>
      </Card>
    </Section>
  );
}
